import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

#handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Correct path to colour - it's in resource.option1
    const colour = (event.detail.resource?.option1 ?? '').toLowerCase();

    this.replaceWith(newMediaGallery);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const updatedGallery = document.querySelector('media-gallery');
        const updatedSlideshow = updatedGallery?.slideshow;
        if (!updatedSlideshow) return;

        const slides = updatedSlideshow.refs?.slides ?? [];
        let targetIndex = 0;

        if (colour === 'black' || colour === 'white') {
          for (let i = 0; i < slides.length; i++) {
            const img = slides[i].querySelector('img');
            const src = (img?.getAttribute('src') ?? '').toLowerCase();
            const srcset = (img?.getAttribute('srcset') ?? '').toLowerCase();

            if (src.includes(colour) || srcset.includes(colour)) {
              targetIndex = i;
              break;
            }
          }
        }

        const targetSlide = slides[targetIndex];
        const slideId = targetSlide?.getAttribute('slide-id');

        if (slideId) {
          updatedSlideshow.select({ id: slideId }, undefined, { animate: false });
        } else {
          updatedSlideshow.select(targetIndex, undefined, { animate: false });
        }
      });
    });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
